import * as React from 'react';
import { CheckboxSize, ResponsiveStyleValue } from '../../theming';
import { FixMeLater } from '../../utils';
import { SwitchBase, SwitchBaseProps } from '../SwitchBase';
import { CHECKBOX_STATIC_STYLES, useCheckboxCssVariables } from './Checkbox.styles';
import { useCheckboxGroupContext } from './CheckboxGroup/CheckboxGroup.context';
import { CheckIcon } from './CheckIcon';

export interface CheckboxProps extends SwitchBaseProps {
    /**
     * The size variant of the component.
     * @default m
     */
    size?: ResponsiveStyleValue<CheckboxSize>;

    /**
     * A reference passed to the `input` element.
     */
    inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * Checkbox represents a classic HTML checkbox. Checkboxes enable users to select multiple options from a group or toggle a single option on or off.
 */
export const Checkbox: React.ForwardRefExoticComponent<CheckboxProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function Checkbox(props: CheckboxProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const { checked, value, onChange, inputRef, error = false, size = 'm', ...other } = props;

        const context = useCheckboxGroupContext();

        const contextProps = context
            ? {
                  checked: context.value.includes(value as string),
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                      context.onChange?.(event);
                      onChange?.(event);
                  },
              }
            : {};

        const cssVariables = useCheckboxCssVariables(size);

        return (
            <div ref={ref} style={cssVariables} css={CHECKBOX_STATIC_STYLES} data-error={error || undefined}>
                <SwitchBase
                    ref={inputRef}
                    type="checkbox"
                    checked={checked}
                    onChange={onChange}
                    value={value}
                    error={error}
                    data-input={true}
                    invisible={false}
                    {...contextProps}
                    {...other}
                />
                <CheckIcon biDirectional={true} aria-hidden={true} />
            </div>
        );
    });
